/* eslint-disable no-undef */
import type {
  ListConversationsResponse,
  GetConversationResponse,
  FeedbackResponse,
} from '../types/agent.types';
import {
  conversationsBaseUrl,
  toChatMessages,
  toConversation,
  type StoredConversation,
  type StoredMessage,
} from './conversationStore';

const AGENT_API_URL = process.env.NEXT_PUBLIC_AGENT_API_URL;

async function getToken(): Promise<string> {
  return localStorage.getItem('ecToken') || '';
}

async function fetchJson<T>(url: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();
  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    throw new Error(`Agent API error: ${response.status}`);
  }

  // 204 No Content
  if (response.status === 204) return undefined as T;

  return response.json();
}

function agentUrl(path: string): string {
  if (!AGENT_API_URL) throw new Error('Agent API URL not configured');
  return `${AGENT_API_URL}${path}`;
}

function conversationsUrl(path = ''): string {
  if (!AGENT_API_URL) throw new Error('Agent API URL not configured');
  return `${conversationsBaseUrl(AGENT_API_URL)}${path}`;
}

export async function listConversations(): Promise<ListConversationsResponse> {
  const data = await fetchJson<{ conversations: StoredConversation[] }>(conversationsUrl());
  const conversations = data.conversations.map(toConversation);
  return { conversations, count: conversations.length };
}

export async function getConversation(conversationId: string): Promise<GetConversationResponse> {
  const data = await fetchJson<{ conversation: StoredConversation; messages: StoredMessage[] }>(
    conversationsUrl(`/${encodeURIComponent(conversationId)}`)
  );
  return {
    conversation: toConversation(data.conversation),
    messages: toChatMessages(data.messages),
  };
}

// The store has no hard delete. Archiving hides the conversation from the list and can be undone.
export async function deleteConversation(conversationId: string): Promise<void> {
  await fetchJson<unknown>(conversationsUrl(`/${encodeURIComponent(conversationId)}/archive`), {
    method: 'POST',
  });
}

export async function submitFeedback(
  conversationId: string,
  messageId: string,
  rating: 'positive' | 'negative',
  comment?: string
): Promise<FeedbackResponse> {
  return fetchJson<FeedbackResponse>(agentUrl(`/conversations/${conversationId}/feedback`), {
    method: 'POST',
    body: JSON.stringify({ messageId, rating, ...(comment && { comment }) }),
  });
}
